import React from "react";
import { Box } from "../design-system";
import { CAPTION_COLOURS, CaptionStyle, REEL } from "./constants/reelText";

interface VerticalPreviewProps {
  thumbnail: string;
  text: string;
  /** Width in px; the height follows at 9:16. */
  width: number;
  captions: boolean;
  captionStyle: CaptionStyle;
  /** This clip will export vertical — the frame glows. */
  chosen?: boolean;
  onClick?: () => void;
  /** Blur = the whole frame over a blurred fill (orientation 'vertical'); Zoom = the
   *  frame fills the screen (orientation 'vertical_fill'). */
  look?: "blur" | "zoom";
}

/**
 * What the clip will look like as a 9:16 export, drawn from the poster alone — nothing is
 * rendered. It copies the worker's vertical recipe (tasks.py `orientation == 'vertical'`):
 * the frame fitted across the width, a zoomed + blurred copy filling above and below, and
 * the captions in the lower third (MarginV 22%), the first three words with the second lit
 * up, the way a word group looks mid-sentence. A portrait poster simply fills the frame.
 *
 * look="zoom" copies the other recipe (`vertical_fill`): the frame fills 9:16 with no
 * bars. The preview crops the middle; the export aims at the face (transcript/reframe.py),
 * so an off-center speaker comes out better than this preview shows, never worse.
 */
const VerticalPreview = ({
  thumbnail,
  text,
  width,
  captions,
  captionStyle,
  chosen = false,
  onClick,
  look = "blur",
}: VerticalPreviewProps) => {
  const isZoom = look === "zoom";
  const hint = isZoom ? REEL.VERTICAL_ZOOM_PREVIEW : REEL.VERTICAL_PREVIEW;
  const height = Math.round((width * 16) / 9);
  // The worker's caption is 96px on a 1080-wide frame (64 at 720, scaled by the short side).
  const fontSize = Math.max(7, Math.round((width * 96) / 1080));
  const outline = Math.max(1, Math.round(fontSize / 8));
  const colours = CAPTION_COLOURS[captionStyle] ?? CAPTION_COLOURS.cherry;
  const words = (text || "").split(/\s+/).filter(Boolean).slice(0, 3);

  return (
    <ChosenFrame chosen={chosen}>
      <Box
        component={onClick ? "button" : "div"}
        onClick={onClick}
        title={hint}
        aria-label={hint}
        aria-pressed={onClick ? chosen : undefined}
        data-testid={isZoom ? "vertical-zoom-preview" : "vertical-preview"}
        sx={{
          display: "block",
          padding: 0,
          border: "none",
          cursor: onClick ? "pointer" : "default",
          position: "relative",
          width: `${width}px`,
          height: `${height}px`,
          flex: "0 0 auto",
          borderRadius: "8px",
          overflow: "hidden",
          backgroundColor: "#000",
        }}
      >
        {thumbnail && isZoom ? (
          <img
            src={thumbnail}
            alt=""
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : thumbnail ? (
          <>
            <img
              src={thumbnail}
              alt=""
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                filter: "blur(8px)",
                transform: "scale(1.15)",
              }}
            />
            <img
              src={thumbnail}
              alt=""
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          </>
        ) : null}
        {captions && words.length ? (
          <Box
            sx={{
              position: "absolute",
              left: "4%",
              right: "4%",
              bottom: "22%",
              textAlign: "center",
              fontFamily: "'DejaVu Sans', Verdana, Arial, sans-serif",
              fontWeight: 700,
              fontSize: `${fontSize}px`,
              lineHeight: 1.15,
              WebkitTextStroke: `${outline}px #000`,
              paintOrder: "stroke fill",
              textShadow: `0 ${Math.max(1, Math.round(fontSize / 24))}px 1px rgba(0,0,0,0.6)`,
            }}
          >
            {words.map((word, index) => (
              <span key={index} style={{ color: index === 1 ? colours.active : colours.base }}>
                {index ? " " : ""}
                {word}
              </span>
            ))}
          </Box>
        ) : null}
      </Box>
      <Box
        component="span"
        aria-hidden
        sx={{
          display: "block",
          textAlign: "center",
          marginTop: "4px",
          fontFamily: "Outfit, sans-serif",
          fontSize: "12px",
          color: chosen ? "#FF6B8B" : "rgba(255,255,255,0.6)",
        }}
      >
        {isZoom ? REEL.LOOK_ZOOM : REEL.LOOK_BLUR}
      </Box>
    </ChosenFrame>
  );
};

/**
 * The red glow around whichever frame (original poster or 9:16 preview) a ticked clip
 * will export as. Unchosen frames get the same padding, transparent, so nothing jumps.
 */
export const ChosenFrame = ({
  chosen,
  children,
}: {
  chosen: boolean;
  children: React.ReactNode;
}) => (
  <Box
    data-chosen={chosen ? "true" : "false"}
    sx={{
      flex: "0 0 auto",
      alignSelf: "flex-start",
      padding: "3px",
      borderRadius: "11px",
      background: chosen
        ? "linear-gradient(135deg, #EC2B57 0%, #FF6B8B 50%, #B0103A 100%)"
        : "transparent",
      boxShadow: chosen ? "0 0 14px rgba(236, 43, 87, 0.55)" : "none",
      transition: "box-shadow 150ms ease, background 150ms ease",
      maxWidth: "100%",
    }}
  >
    {children}
  </Box>
);

export default VerticalPreview;
